import type {
  SlotsResponse,
  BookResponse,
  BookSlotBody,
  ManageResponse,
  RescheduleResponse,
  RescheduleBody,
  CancelResponse,
  ErrorResponse,
} from "./types";

const BASE_URL = process.env.NEXT_PUBLIC_BACKEND_API_URL ?? "";

/**
 * A deployed build that was given no backend URL.
 *
 * `?? ""` above makes every request RELATIVE, so it lands on this app's own
 * origin — `/api/public/book/...` on the host that proxies `/book`. That path
 * serves an HTML 404, `res.json()` throws on it, and the catch below reports
 * "Something went wrong. Please try again." So the one failure that is purely
 * configuration looks exactly like a backend outage, and nothing on the page or
 * in the console says which.
 *
 * `NEXT_PUBLIC_*` is inlined at BUILD time, so this cannot be fixed by setting
 * the variable in Vercel — the environment has to be rebuilt. That is the whole
 * reason it deserves its own message rather than the generic one: it is the
 * likely first symptom of a new environment (an AWS/Azure move, a new preview
 * target) whose variable was never set, or was set after the last build.
 *
 * Not applied under test: `api.test.ts` deliberately runs with an empty base so
 * it can assert relative request paths.
 */
const isMissingBackendUrl = !BASE_URL && process.env.NODE_ENV !== "test";

async function apiRequest<T>(
  path: string,
  options?: RequestInit
): Promise<T | ErrorResponse> {
  if (isMissingBackendUrl) {
    // Console as well as the returned error: the panel a lead sees must not
    // name our infrastructure, but whoever opens devtools should not have to
    // guess.
    console.error(
      "[scheduler-app] NEXT_PUBLIC_BACKEND_API_URL is not set in this build. " +
        "Set it for this environment and REDEPLOY — the value is inlined at build time."
    );
    return {
      error: "BACKEND_URL_NOT_CONFIGURED",
      message: "Booking is unavailable right now. Please try again later.",
    } as ErrorResponse;
  }

  try {
    const res = await fetch(`${BASE_URL}${path}`, {
      headers: { "Content-Type": "application/json" },
      // Every one of these reads link state that an AE can change while a lead
      // is looking at the page — pause, resume, cancel, reschedule. The
      // backend sends no `Cache-Control`, and a 410 is one of the statuses
      // RFC 9111 lets a browser cache HEURISTICALLY with no explicit freshness
      // header at all, so a paused link's refusal could be replayed from the
      // browser's own cache after the AE had already resumed it. That is the
      // reported bug: resume, reopen the URL, still "paused" until a HARD
      // reload (which is exactly the reload that bypasses the HTTP cache).
      //
      // Before `...options`, matching `headers` above: a future caller with a
      // real reason to cache can still say so, rather than having to fight the
      // transport. Nothing overrides it today.
      cache: "no-store",
      ...options,
    });
    const data = await res.json();
    if (!res.ok) {
      return data as ErrorResponse;
    }
    return data as T;
  } catch {
    return {
      error: "UNEXPECTED_ERROR",
      message: "Something went wrong. Please try again.",
    };
  }
}

export function isErrorResponse(res: unknown): res is ErrorResponse {
  return typeof res === "object" && res !== null && "error" in res;
}

/**
 * `timezone` is optional (kris-backend#9713): omit it and the backend answers in
 * the zone pinned on the link, falling back to the AE's own. Passing `undefined`
 * — or `""`, which is never a valid IANA zone — drops the param entirely rather
 * than sending an empty value the backend would reject.
 *
 * It stays a positional parameter so every existing call site keeps working
 * unchanged; only the callers that want the backend to decide pass `undefined`.
 */
export async function getSlots(
  orgId: string,
  token: string,
  timezone: string | undefined,
  date: string,
  durationMinutes?: number
): Promise<SlotsResponse | ErrorResponse> {
  const params = new URLSearchParams();
  if (timezone) {
    params.set("timezone", timezone);
  }
  params.set("date", date);
  if (durationMinutes !== undefined) {
    params.set("durationMinutes", String(durationMinutes));
  }
  return apiRequest<SlotsResponse>(
    `/api/public/book/${orgId}/${token}/slots?${params.toString()}`
  );
}

export async function bookSlot(
  orgId: string,
  token: string,
  body: BookSlotBody
): Promise<BookResponse | ErrorResponse> {
  return apiRequest<BookResponse>(`/api/public/book/${orgId}/${token}`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function getManage(
  orgId: string,
  token: string
): Promise<ManageResponse | ErrorResponse> {
  return apiRequest<ManageResponse>(
    `/api/public/book/${orgId}/${token}/manage`
  );
}

/**
 * Move a booking, or release it so the lead can pick again.
 *
 * `token` is the BOOKING's manage token — the one in the lead's URL — for both
 * flows. The link token that `/manage` hands back is for `/slots`, not for this.
 *
 * With a `body` the booking moves IN PLACE, keeping its id, manage secret and
 * calendar event. Without one the backend falls back to the pre-split flow:
 * retire the link, mint a fresh one, email it. That fallback is refused on a
 * reusable link — retiring it would take the AE's signature URL down for
 * everyone holding it — so a reusable link MUST send a body, and a bodyless
 * call against one is the 400 INVALID_REQUEST that made reschedule look broken.
 */
export async function reschedule(
  orgId: string,
  token: string,
  body?: RescheduleBody
): Promise<RescheduleResponse | ErrorResponse> {
  return apiRequest<RescheduleResponse>(
    `/api/public/book/${orgId}/${token}/reschedule`,
    {
      method: "POST",
      // Genuinely absent, not `"undefined"`: the legacy path is selected by the
      // ABSENCE of a body, so it has to stay byte-for-byte the request it was.
      // `durationMinutes: undefined` inside the body drops out of the JSON too,
      // which is how "keep the booked length" is expressed on the wire.
      body: body ? JSON.stringify(body) : undefined,
    }
  );
}

export async function cancelBooking(
  orgId: string,
  token: string
): Promise<CancelResponse | ErrorResponse> {
  return apiRequest<CancelResponse>(
    `/api/public/book/${orgId}/${token}/cancel`,
    {
      method: "POST",
    }
  );
}
